import { rem } from 'polished'

import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import * as React from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { useAppDispatch } from 'hooks/reduxHooks'
import Pane from 'components/Common/Pane'
import CurrencyContext from 'contexts/currencyContext'
import ExperienceTicketSelection from '../ExperienceTickets/ExperienceTicketSelection'
import ExperienceTicketInputs from '../ExperienceTickets/ExperienceTicketInputs'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import type { ExperienceDetailsForm } from '../ExperienceDetailsModal/ExperienceDetailsBookingModal'
import { formToObject } from 'lib/forms/formToObject'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { arrayToObject, isNonNullable, sum, max } from 'lib/array/arrayUtils'
import { generateExperienceCheckoutItem } from 'lib/checkout/experiences/cart'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import ExperiencePageStateContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateContext'
import ExperiencePageDispatchContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageDispatchContext'
import { ExperienceOfferPageActions } from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateReducer'
import { arrayToStringListFormat } from 'lib/string/stringUtils'
import { addExperienceToCheckout } from 'checkout/lib/analytics/checkoutUAMappers'
import { addGTMEvent, fireEcommerceGTMEvent } from 'api/googleTagManager'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { addToCartGTM } from 'analytics/eventDefinitions'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { generateExperienceGiftCardItem } from 'lib/checkout/gift/cart'
import type { FetchDateParams } from 'lib/experiences/experienceUtils'
import { getTotalSeats, hasMissingRequiredExperienceTickets } from 'lib/experiences/experienceUtils'
import ExperienceTicketCustomerLimit from '../ExperienceTickets/ExperienceTicketCustomerLimit'
import useExperienceAvailableDates from 'hooks/ExperiencePage/useExperienceAvailableDates'
import useExperienceAvailableTimes from 'hooks/ExperiencePage/useExperienceAvailableTimes'
import { useExperienceTicketsOptions } from 'hooks/ExperiencePage/useExperienceTicketsOptions'
import usePrevious from 'hooks/usePrevious'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { addToUserCart } from 'actions/UserCartActions'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { isMultiCartExperienceSupported } from 'lib/checkout/userCart'
import { addToCart, addToCartMultiItem } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { queryKeyDate } from 'constants/url'
import ExperienceOfferPageBuyBar from './ExperienceOfferPageBuyBar'
import { useWeddingGiftRegistryContext } from 'weddings/contexts/WeddingGiftRegistryContext'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import ArenaSeatSelection from './ArenaSeatSelection'
import {
  getArenaProviderBookingData,
  getArenaTicketCounts,
} from './arenaSeatSelectionUtils'
import type { ArenaSelectionResult } from './arenaSeatSelectionUtils'
import config from 'constants/config'
import { createBookingReservation, releaseBookingReservation } from 'api/experiences'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import ModalContext from 'contexts/ModalContext'
import ExperienceIngressoSeatTextWarningModal, {
  type ExperienceIngressoSeatTextWarningModalResult,
  type IngressoSeatTextWarningGroup,
} from './ExperienceIngressoSeatTextWarningModal'
import { getExperienceReservationMetadata } from 'lib/experiences/experienceReservationUtils'
import { type ExperienceEventEntity } from 'analytics/snowplow/helpers'

const TicketContainer = styled(Pane)`
  padding: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  position: relative;
`

const TicketContainerWithoutPadding = styled(TicketContainer)`
  padding: 0;
`

const TicketInputContainer = styled.div`
  padding: ${rem(24)};
`

type SubmitAction = 'buyNow' | 'keepBrowsing' | 'addToUserCart' | 'addToWeddingGiftRegistryCart'

function ticketModeMapsEqual(
  left: Record<string, string> | undefined,
  right: Record<string, string> | undefined,
) {
  const leftEntries = Object.entries(left ?? {}).filter(([, value]) => !!value)
  const rightMap = Object.fromEntries(Object.entries(right ?? {}).filter(([, value]) => !!value))
  return leftEntries.length === Object.keys(rightMap).length && leftEntries.every(([key, value]) => rightMap[key] === value)
}

function isArenaExperienceOffer(experience: App.ExperienceOffer, selectedDate?: App.ExperienceAvailabilityDate) {
  return experience.provider === 'ing' && !!experience.providerId && selectedDate?.providerData?.selectionMode === 'seat-map'
}

function isBestAvailableIngressoOffer(experience: App.ExperienceOffer, selectedDate?: App.ExperienceAvailabilityDate) {
  return experience.provider === 'ing' && !!experience.providerId && selectedDate?.providerData?.selectionMode === 'best-available'
}

function getIngressoPerformanceWarning(slot?: App.ExperienceAvailabilityTimeSlot) {
  if (!slot?.providerData?.hasPerfNames) return undefined
  return slot.providerData.performanceDescription?.trim() || undefined
}

function getIngressoSeatTextWarningGroups(
  item: App.Checkout.ExperienceItem,
  reservationItems: Array<{ seatText?: string }>,
): Array<IngressoSeatTextWarningGroup> {
  let reservationIndex = 0

  return item.tickets.flatMap(cartTicket => {
    const warningTexts: Array<string> = []

    for (let i = 0; i < cartTicket.count; i += 1) {
      const seatText = reservationItems[reservationIndex]?.seatText?.trim()
      reservationIndex += 1

      if (seatText && !warningTexts.includes(seatText)) {
        warningTexts.push(seatText)
      }
    }

    return warningTexts.length ? [{ ticketName: cartTicket.name, warningTexts }] : []
  })
}

function getReservationWarningsForSnackbar(
  reservation: { items: Array<{ seatText?: string }>; warnings?: Array<{ text: string; ticketIndex: number }> },
) {
  if (!Array.isArray(reservation.warnings)) return []

  const inlineSeatTexts = new Set(
    reservation.items
      .map(item => item.seatText?.trim())
      .filter((seatText): seatText is string => !!seatText),
  )

  return reservation.warnings
    .map(warning => warning.text?.trim())
    .filter((warningText): warningText is string => !!warningText && !inlineSeatTexts.has(warningText))
}

function releaseIngressoReservationHold(experienceId: string, reservationToken?: string) {
  if (!reservationToken) return
  releaseBookingReservation(experienceId, reservationToken).catch(() => undefined)
}

interface ReserveItemResult {
  item: App.Checkout.ExperienceItem
  seatTextWarningGroups: Array<IngressoSeatTextWarningGroup>
  independentWarnings: Array<string>
}

interface Props {
  experienceView: App.ExperienceBookingView
  isBuyNowBookLater?: boolean
  isGift?: boolean
  luxPlusEnabled: boolean
  secondaryAction?: React.ReactElement
  showAddToCartAction?: boolean
  checkoutSource?: App.CheckoutSource
  preserveSelectedDateWhenDateOptionsEmpty?: boolean
  calendarPresentation?: 'dropdown' | 'modal'
  viewMoreToggleTopSpacing?: boolean
}

const ExperienceOfferPageTicketSelection = forwardRef<HTMLFormElement, Props>((props, formRef) => {
  const {
    experienceView,
    isBuyNowBookLater = false,
    isGift = false,
    luxPlusEnabled,
    secondaryAction,
    showAddToCartAction = true,
    checkoutSource,
    preserveSelectedDateWhenDateOptionsEmpty = false,
    calendarPresentation = 'dropdown',
    viewMoreToggleTopSpacing = false,
  } = props

  const { search } = useLocation()
  const initialDateFromUrl = useRef(parseSearchString(search)[queryKeyDate])
  const internalFormRef = useRef<HTMLFormElement>(null)
  const [hasNoTicketsSelected, setHasNoTicketsSelected] = useState(false)
  const [hasRequiredTicketsError, setHasRequiredTicketsError] = useState(false)
  const [isReservingTickets, setIsReservingTickets] = useState(false)
  const isSubmittingReservation = useRef(false)
  useImperativeHandle(formRef, () => internalFormRef.current!)
  const currency = useContext(CurrencyContext)
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const experience = experienceView.originalExperience
  const { isActive: isWeddingGiftRegistry, addItems: addItemsToWeddingGiftRegistryCart } = useWeddingGiftRegistryContext()
  const isWeddingGiftRegistryTicketSelection = isWeddingGiftRegistry && !isBuyNowBookLater && !isGift

  const pageState = useContext(ExperiencePageStateContext)
  const { date, time, pickupPoint: selectedPickupPoint, ticketCounts, redemptionLocation: selectedLocation, language: selectedLanguage, ticketModesMap: selectedTicketModesMap } = pageState

  const collinsonValidityNote = experience.provider === 'cln' ? (
    <BodyTextBlock variant="medium" startIcon={<LineCheckCircleIcon colour="success" size="S" />} style={{ marginTop: rem(8) }}>
      {intl.formatMessage({
        id: 'EmENifCHWIuwd5PYzMli',
        defaultMessage: 'Valid for 12 months from the selected date · Date cannot be changed.',
        description: 'Validity note for Collinson lounge passes after the customer selects a lounge access date',
      })}
    </BodyTextBlock>
  ) : undefined

  const userMultiItemCartSupported = showAddToCartAction && isMultiCartExperienceSupported(experience, isGift)

  const trackExperienceOfferEvent = useTrackExperienceOfferEvent()
  const getTicketEventContext = useCallback((ticket: App.ExperienceBookingTicketView): Array<ExperienceEventEntity> => {
    return [{
      type: 'item',
      data: {
        offer: experience,
        itemProps: {
          itemId: ticket.id,
          typeId: ticket.name,
          price: ticket.price,
          ...(!isBuyNowBookLater && !isGift && {
            travelStart: date,
            travelEnd: date,
          }),
        },
      },
    }]
  }, [experience, date, isBuyNowBookLater, isGift])

  const handleOptionPurchaseEvent = useCallback(() => {
    if (isBuyNowBookLater) {
      return trackExperienceOfferEvent('BNBL_BUY_NOW_CLICKED')
    }
    if (isGift) {
      return trackExperienceOfferEvent('GIFT_BUY_NOW_CLICKED')
    }
    return trackExperienceOfferEvent('BUY_NOW_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackExperienceOfferEvent is stable
  }, [isBuyNowBookLater, isGift])

  const onTicketCountIncrease = useCallback((ticket: App.ExperienceBookingTicketView) => {
    if (isBuyNowBookLater) {
      return trackExperienceOfferEvent('BNBL_INCREASE_OPTION_SELECTED', getTicketEventContext(ticket))
    }
    if (isGift) {
      return trackExperienceOfferEvent('GIFT_INCREASE_OPTION_SELECTED', getTicketEventContext(ticket))
    }
    return trackExperienceOfferEvent('INCREASE_OPTION_SELECTED', getTicketEventContext(ticket))
  }, [isBuyNowBookLater, isGift, getTicketEventContext]) // oxlint-disable-line react-hooks/exhaustive-deps -- trackExperienceOfferEvent is stable

  const onTicketCountDecrease = useCallback((ticket: App.ExperienceBookingTicketView) => {
    if (isBuyNowBookLater) {
      return trackExperienceOfferEvent('BNBL_DECREASE_OPTION_SELECTED', getTicketEventContext(ticket))
    }
    if (isGift) {
      return trackExperienceOfferEvent('GIFT_DECREASE_OPTION_SELECTED', getTicketEventContext(ticket))
    }
    return trackExperienceOfferEvent('DECREASE_OPTION_SELECTED', getTicketEventContext(ticket))
  }, [isBuyNowBookLater, isGift, getTicketEventContext]) // oxlint-disable-line react-hooks/exhaustive-deps -- trackExperienceOfferEvent is stable

  const onTicketCountChange = useCallback((ticket: App.ExperienceBookingTicketView, increment: boolean) => {
    if (increment) {
      return onTicketCountIncrease(ticket)
    }
    return onTicketCountDecrease(ticket)
  }, [onTicketCountDecrease, onTicketCountIncrease])

  const experienceDispatch = useContext(ExperiencePageDispatchContext)

  const setTicketCounts = useCallback((ticketCounts: Record<string, number>) => experienceDispatch({ type: ExperienceOfferPageActions.setTicketCounts, ticketCounts }), [experienceDispatch])

  const setDate = useCallback((date?: string) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setDate, date })
  }, [experienceDispatch])

  const setSelectedPickupPoint = useCallback((pickupPoint?: App.ExperiencePickupPoint) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setPickupPoint, pickupPoint })
  }, [experienceDispatch])

  const setTimeSlot = useCallback((time: string) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setTime, time })
  }, [experienceDispatch])

  const setSelectedLocation = useCallback((redemptionLocation?: App.ExperienceRedemptionLocation) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setRedemptionLocation, redemptionLocation })
  }, [experienceDispatch])

  const setLanguage = useCallback((language?: App.ExperienceLanguage) => {
    if (!language) return
    experienceDispatch({ type: ExperienceOfferPageActions.setLanguage, language })
  }, [experienceDispatch])

  const setSelectedTicketMode = useCallback((ticketModesMap?: Record<string, string>) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setTicketMode, ticketModesMap })
  }, [experienceDispatch])

  const fetchDateParams = useMemo(() => ({
    pickupPointId: selectedPickupPoint?.id,
    redemptionLocationId: selectedLocation?.id,
    ticketModesMap: selectedTicketModesMap,
    isBuyNowBookLater,
    isGift,
  }), [selectedPickupPoint, selectedLocation, selectedTicketModesMap, isBuyNowBookLater, isGift])
  const [availableDates, fetchingDates, datesError] = useExperienceAvailableDates(experienceView.originalExperience, fetchDateParams)

  const fetchTimeParams = useMemo(() => ({
    date: isBuyNowBookLater || isGift ? undefined : date,
    redemptionLocationId: selectedLocation?.id,
    pickupPointId: selectedPickupPoint?.id,
    isBuyNowBookLater,
    isGift,
    ticketModesMap: selectedTicketModesMap,
  }), [selectedPickupPoint, selectedLocation, selectedTicketModesMap, isBuyNowBookLater, isGift, date])
  const [availableTimes, fetchingTimeSlots, timeSlotsError] = useExperienceAvailableTimes(experienceView.originalExperience, fetchTimeParams)

  const loading = !!(fetchingDates || fetchingTimeSlots)

  const currentTimeSlot = useMemo(() => {
    if (!availableTimes) return undefined

    if (isBuyNowBookLater || isGift) {
      let timeslots = availableTimes.filter(slot => slot.purchaseLimit.maxReason !== 'AVAILABILITY')
      if (!timeslots.length) timeslots = availableTimes

      return max<App.ExperienceAvailabilityTimeSlot>(timeslots, slot => slot.purchaseLimit.max)
    }

    return availableTimes.find(slot => slot.time === time)
  }, [availableTimes, time, isBuyNowBookLater, isGift])

  const previousAvailableDates = usePrevious(availableDates)

  const loadDates = useCallback(() => {
    if (!experience) return

    const fetchParams: FetchDateParams = {}

    const locations = experience.redemptionLocations
    if (locations?.length === 1) {
      const defaultLocation = locations[0]
      setSelectedLocation(defaultLocation)
      fetchParams.redemptionLocationId = defaultLocation.id
      trackExperienceOfferEvent('REDEMPTION_LOCATION_SELECTED_NON_INTERACTIVE')
    }

    const pickups = experience.pickupPoints
    if (pickups?.length === 1) {
      const defaultPickup = pickups[0]
      setSelectedPickupPoint(defaultPickup)
      fetchParams.pickupPointId = defaultPickup.id
      trackExperienceOfferEvent('PICKUP_LOCATION_SELECTED_NON_INTERACTION')
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [experience, isBuyNowBookLater, isGift, selectedPickupPoint, date, setSelectedPickupPoint, trackExperienceOfferEvent, setSelectedLocation, setSelectedTicketMode])

  useEffect(() => {
    loadDates()
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceView])

  useEffect(() => {
    if (!availableDates?.length) return

    if (!experience.hasCalendar) {
      // Gift/BNBL modals share this context — setting date to undefined here
      // would wipe the date the main page depends on, causing tickets to vanish.
      // These modals don't need a context date (fetchTimeParams overrides it).
      if (isBuyNowBookLater || isGift) return

      // we still fetch calendar data even though it doesn't have it as we need it
      // to go fetch the times which gives us the tickets.
      // setDate clears ticketCounts + time in the reducer. Preserve any pre-loaded
      // values (e.g. from saved trip planner data) and restore them afterwards so
      // the auto-select guard in onSelectTimeSlot fires correctly and doesn't wipe
      // the restored counts on the next render cycle.
      const savedCounts = ticketCounts
      const savedTime = time
      setDate(availableDates[0].day)
      if (savedTime) {
        setTimeSlot(savedTime)
      }
      if (Object.keys(savedCounts).length > 0) {
        setTicketCounts(savedCounts)
      }
      trackExperienceOfferEvent('DATE_ON_CALENDAR_SELECTED_NON_INTERACTION')
      return
    }

    const urlDate = initialDateFromUrl.current
    initialDateFromUrl.current = undefined
    if (urlDate && urlDate !== date && availableDates.some(availableDate => availableDate.day === urlDate)) {
      setDate(urlDate)
    }
  }, [availableDates]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // After login the availableDates and availableTimes gets cleared
    // in the context, this will fire a refetch of dates and times
    // to update the limits for the new customer
    if (previousAvailableDates?.length && !availableDates?.length) {
      loadDates()
    }
  }, [previousAvailableDates, availableDates, loadDates])

  const onPickupPointSelect = useCallback((newPickupPoint?: App.ExperiencePickupPoint) => {
    if (newPickupPoint && selectedPickupPoint && newPickupPoint.id === selectedPickupPoint.id) return

    setSelectedPickupPoint(newPickupPoint)
    setDate()
    setTimeSlot('')
    setTicketCounts({})
  }, [setSelectedPickupPoint, selectedPickupPoint, setDate, setTimeSlot, setTicketCounts])

  const onLocationSelect = useCallback((newLocation?: App.ExperienceRedemptionLocation) => {
    if (newLocation && selectedLocation && newLocation.id === selectedLocation.id) return
    setSelectedLocation(newLocation)
    setDate()
    setTimeSlot('')
    setTicketCounts({})
  }, [setSelectedLocation, selectedLocation, setDate, setTimeSlot, setTicketCounts])

  const onTicketModeSelect = useCallback((newTicketModesMap?: Record<string, string>) => {
    if (ticketModeMapsEqual(newTicketModesMap, selectedTicketModesMap)) return

    setSelectedTicketMode(newTicketModesMap)
    setDate()
    setTimeSlot('')
    setTicketCounts({})
  }, [setSelectedTicketMode, selectedTicketModesMap, setDate, setTimeSlot, setTicketCounts])

  const onDateSelect = useCallback((newDate: App.ExperienceAvailabilityDate) => {
    if (date === newDate.day) return

    setDate(newDate.day)
    setTimeSlot('')
    setTicketCounts({})
  }, [setDate, date, setTimeSlot, setTicketCounts])

  const onSelectTimeSlot = useCallback((newTimeSlot: string) => {
    if (newTimeSlot === time) return
    // setTime clears ticketCounts in the reducer. When the time transitions from
    // unset to a slot (e.g. the non-interactive auto-select of the hidden single
    // timeslot on open-dated offers), preserve any pre-loaded counts (saved trip
    // planner data) and restore them — mirroring the no-calendar date restore above.
    // Counts only need wiping when changing between time slots.
    const savedCounts = !time && Object.keys(ticketCounts).length > 0 ? ticketCounts : undefined
    setTimeSlot(newTimeSlot)
    setTicketCounts(savedCounts ?? {})
  }, [setTimeSlot, time, ticketCounts, setTicketCounts])

  const handleTicketCountChange = useCallback((ticket: App.ExperienceBookingTicketView, nextCount: number) => {
    if (hasNoTicketsSelected) setHasNoTicketsSelected(false)
    if (hasRequiredTicketsError) setHasRequiredTicketsError(false)
    setTicketCounts({
      ...ticketCounts,
      [ticket.id]: nextCount,
    })
    onTicketCountChange(ticket, (ticketCounts[ticket.id] ?? 0) < nextCount)
  }, [setTicketCounts, ticketCounts, onTicketCountChange, hasNoTicketsSelected, hasRequiredTicketsError])

  const allTicketOptions = useExperienceTicketsOptions(availableTimes, { isBuyNowBookLater, isGift, time })

  const hasMissingRequiredTickets = useMemo(
    () => hasMissingRequiredExperienceTickets(allTicketOptions, ticketCounts),
    [allTicketOptions, ticketCounts],
  )

  const counts = useMemo(() => {
    return Object.entries(ticketCounts).map(([ticketId, count]) => {
      return {
        ticket: allTicketOptions.find(t => t.id === ticketId)!,
        count,
      }
      // sometimes the tickets we previously selected are no longer available
      // remove them
    }).filter(count => !!count.ticket)
  }, [ticketCounts, allTicketOptions])

  const { totalNonExtraCountOnCart, totalCountOnCart } = useMemo(() => ({
    totalCountOnCart: sum(counts, count => count.count),
    totalNonExtraCountOnCart: sum(counts.filter(item => !item.ticket.isExtra), count => count.count),
  }), [counts])

  const [isFromPrice, totalPrice] = useMemo(() => {
    const ticketsById = arrayToObject(
      allTicketOptions,
      (ticket) => ticket.id,
    )
    const selectedTicketPrices = Object.entries(ticketCounts).flatMap(
      ([id, count]) => (ticketsById[id]?.price ?? 0) * count,
    )
    const selectedPrice = sum(selectedTicketPrices)
    return [
      selectedPrice === 0,
      selectedPrice || experienceView.price || 0,
    ]
  }, [ticketCounts, experienceView, allTicketOptions])

  const totalLuxPlusPrice = useMemo(() => {
    const ticketsById = arrayToObject(
      allTicketOptions,
      (ticket) => ticket.id,
    )
    const selectedTicketPrices = Object.entries(ticketCounts).flatMap(
      ([id, count]) => (ticketsById[id]?.memberPrice ?? 0) * count,
    )
    const selectedPrice = sum(selectedTicketPrices)
    return selectedPrice || experience.pricing.memberPrice
  }, [ticketCounts, allTicketOptions, experience.pricing.memberPrice])

  const orderMode = useMemo<App.ExperienceOrderMode>(() => {
    switch (true) {
      case isGift && experience.giftType === 'offer':
        return 'gift-offer'
      case isGift && experience.giftType === 'gift-card':
        return 'gift-card'
      case isBuyNowBookLater:
        return 'bnbl'
      default:
        return 'booking'
    }
  }, [isGift, isBuyNowBookLater, experience.giftType])

  const generateCartItem = useCallback((ticketData: ExperienceDetailsForm) => {
    if (isGift && experience.giftType === 'gift-card') {
      return generateExperienceGiftCardItem(
        experience,
        totalPrice,
        intl,
        counts,
      )
    } else {
      return generateExperienceCheckoutItem(
        experience,
        ticketData,
        counts,
        {
          isBuyNowBookLater,
          isGift,
          orderMode,
        },
      )
    }
  }, [counts, experience, intl, isBuyNowBookLater, isGift, totalPrice, orderMode])

  const reserveItem = useCallback(async(item: App.Checkout.ExperienceItem): Promise<ReserveItemResult | undefined> => {
    try {
      const bookingItems = item.tickets.flatMap(checkoutTicket => {
        const ticket = allTicketOptions.find(ticketOption => ticketOption.id === checkoutTicket.ticketId)
        const ticketCount = Number(checkoutTicket.count)
        if (!ticket || !Number.isFinite(ticketCount) || ticketCount <= 0) return []

        return Array.from({ length: ticketCount }, () => ({
          experienceItemId: item.itemId,
          experienceOfferId: experience.id,
          fareType: ticket.fareType || ticket.name,
          productId: ticket.productId,
          salePrice: ticket.price,
        }))
      })

      if (!bookingItems.length) return undefined

      const reservation = await createBookingReservation(bookingItems)
      const rejected = reservation?.items.find(reservationItem => reservationItem.status === 'rejected')
      const reservationUnavailableMessage = intl.formatMessage({
        id: 'FEWI-rasRAj4He9pDW5c',
        defaultMessage: 'These tickets can no longer be reserved. Please choose a different option.',
        description: 'Error shown when an experience reservation cannot be created before checkout',
      })

      if (!reservation?.items?.length || !reservation.reservationToken || rejected) {
        releaseIngressoReservationHold(experience.id, reservation?.reservationToken)
        showSnackbar(rejected?.messages?.[0]?.text ?? reservationUnavailableMessage, 'critical')
        return undefined
      }

      if (reservation.items.length !== bookingItems.length) {
        releaseIngressoReservationHold(experience.id, reservation.reservationToken)
        showSnackbar(reservationUnavailableMessage, 'critical')
        return undefined
      }

      const independentWarnings = getReservationWarningsForSnackbar(reservation)
      const seatTextWarningGroups = getIngressoSeatTextWarningGroups(item, reservation.items)

      return {
        item: {
          ...item,
          metaData: {
            ...item.metaData,
            providerBookingData: {
              reservationExpiresAt: reservation.expiresAt,
              reservationToken: reservation.reservationToken,
              returnUrl: getExperienceOfferPageURL(experience),
            },
          },
        },
        seatTextWarningGroups,
        independentWarnings,
      }
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'WfXCV78DOpd7Ot86aiE7',
        defaultMessage: 'We could not reserve these tickets. Please try again.',
        description: 'Error shown when an experience reservation request fails before checkout',
      }), 'critical')
      return undefined
    }
  }, [allTicketOptions, experience, intl])

  const appendToUserCart = useCallback(async(ticketData: ExperienceDetailsForm) => {
    const item = generateCartItem(ticketData)
    dispatch(addToUserCart([item]))
    trackEvent(addToCartMultiItem([item], { isMultiCart: true }))
    return true
  }, [dispatch, generateCartItem])

  const appendToWeddingGiftRegistryCart = useCallback((ticketData: ExperienceDetailsForm) => {
    const item = generateCartItem(ticketData)
    addItemsToWeddingGiftRegistryCart([item], {
      onAdd: (itemsReadyToAdd) => {
        trackEvent(addToCartMultiItem(itemsReadyToAdd, { isMultiCart: true }))
      },
    })
  }, [addItemsToWeddingGiftRegistryCart, generateCartItem])

  const proceedToCheckout = useCallback(async(ticketData: ExperienceDetailsForm, keepBrowsing = false) => {
    let item = generateCartItem(ticketData)
    const selectedAvailableDate = availableDates.find(availableDate => availableDate.day === date)
    const isIngressoBestAvailable = isBestAvailableIngressoOffer(experience, selectedAvailableDate)
    if (isIngressoBestAvailable && !isGift && !isBuyNowBookLater && !keepBrowsing) {
      setIsReservingTickets(true)
      try {
        if (item.itemType !== 'experience') return false
        const reserveResult = await reserveItem(item)
        if (!reserveResult) return false

        if (reserveResult.seatTextWarningGroups.length > 0) {
          const modalResult = await showModal<ExperienceIngressoSeatTextWarningModalResult>(<ExperienceIngressoSeatTextWarningModal
            warningGroups={reserveResult.seatTextWarningGroups}
            additionalWarnings={reserveResult.independentWarnings}
          />)

          if (modalResult !== 'continue') {
            const reservationToken = getExperienceReservationMetadata(reserveResult.item)?.reservationToken
            releaseIngressoReservationHold(experience.id, reservationToken)
            return false
          }
        } else {
          reserveResult.independentWarnings.forEach(warningText => {
            showSnackbar(warningText, 'warning')
          })
        }

        item = reserveResult.item
      } finally {
        setIsReservingTickets(false)
      }
    }
    const initParams = isGift || checkoutSource ? {
      ...(isGift ? { isGift } : {}),
      ...(checkoutSource ? { checkoutSource } : {}),
    } : undefined
    dispatch(addToCheckoutCart([item], initParams))
    trackEvent(addToCart())
    if (keepBrowsing) {
      showSnackbar(intl.formatMessage({
        id: '8lxFOePJ8_3yc7gdvI8H',
        defaultMessage: 'Your item has been added to the cart.',
        description: 'Success message shown after adding an experience to the cart',
      }), 'success')
      return true
    }
    dispatch(navigateToCheckout())
    return true
  }, [generateCartItem, dispatch, isGift, isBuyNowBookLater, checkoutSource, intl, experience, availableDates, date, reserveItem, showModal])

  const submitForm = useCallback(async(submitAction: SubmitAction) => {
    if (isSubmittingReservation.current) return
    isSubmittingReservation.current = true
    try {
      if (!internalFormRef.current?.reportValidity()) return
      if (hasMissingRequiredTickets) {
        setHasRequiredTicketsError(true)
        return
      }
      if (totalNonExtraCountOnCart === 0 || minSeats > totalUsedSeatsOnCart) {
        setHasNoTicketsSelected(true)
        return
      }
      handleOptionPurchaseEvent()
      const ticketData = formToObject<ExperienceDetailsForm>(internalFormRef.current)
      delete ticketData.ticketCount // Unneeded field
      counts.map(ticket => {
        addGTMEvent(addExperienceToCheckout(experienceView, ticket, currency))
      })

      const generatedTicketData = isBuyNowBookLater || isGift ?
          {
            date: undefined,
            language: selectedLanguage?.id,
            pickupPoint: selectedPickupPoint?.id,
            redemptionLocation: selectedLocation?.id,
            ticketModesMap: selectedTicketModesMap,
            time: ticketData.time,
          } : {
            ...ticketData,
            ticketModesMap: selectedTicketModesMap,
          }

      let didAddToCart = false
      if (submitAction === 'addToWeddingGiftRegistryCart') {
        appendToWeddingGiftRegistryCart(generatedTicketData)
        didAddToCart = true
      } else if (submitAction === 'addToUserCart') {
        didAddToCart = await appendToUserCart(generatedTicketData)
      } else {
        didAddToCart = await proceedToCheckout(generatedTicketData, submitAction === 'keepBrowsing')
      }

      if (!didAddToCart) return

      const ticketQuantity = sum(counts ?? [], count => count.count)
      fireEcommerceGTMEvent(addToCartGTM(experience, currency, totalPrice, ticketQuantity, 'experience'))
    } finally {
      isSubmittingReservation.current = false
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- onBuyNow is stable
  }, [counts, experience, currency, totalPrice, experienceView, isBuyNowBookLater, isGift, appendToWeddingGiftRegistryCart, appendToUserCart, proceedToCheckout, selectedLanguage?.id, selectedLocation?.id, selectedPickupPoint?.id, selectedTicketModesMap, hasMissingRequiredTickets])

  const handleBuyNow = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    submitForm('buyNow')
  }, [submitForm])

  const handleAddToUserCart = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    submitForm('addToUserCart')
  }, [submitForm])

  const handleAddToWeddingGiftRegistryCart = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    submitForm('addToWeddingGiftRegistryCart')
  }, [submitForm])
  const locationFieldLabel = intl.formatMessage({
    id: '96bXOVYIhWfQs3KxATGI',
    defaultMessage: 'location',
    description: 'Field label used in no-tickets messaging for the selected location on the experience page',
  })
  const dateFieldLabel = intl.formatMessage({
    id: 'ySIT44W-SB6W6dYa12jN',
    defaultMessage: 'date',
    description: 'Field label used in no-tickets messaging for the selected date on the experience page',
  })
  const timeFieldLabel = intl.formatMessage({
    id: 'EE2NHYPq_uSPCaHrMC9i',
    defaultMessage: 'time',
    description: 'Field label used in no-tickets messaging for the selected time on the experience page',
  })
  const languageFieldLabel = intl.formatMessage({
    id: 'hacB3cdh6kRRsjF5IoaA',
    defaultMessage: 'language',
    description: 'Field label used in no-tickets messaging for the selected language on the experience page',
  })

  const userSelectedFields = useMemo(() => {
    const fields: Array<string> = []
    if (experience.redemptionLocations.length > 1 || experience.pickupPoints.length > 1) {
      fields.push(locationFieldLabel)
    }
    if (experience.hasCalendar && !datesError) {
      fields.push(dateFieldLabel)

      if (experience.hasTimeslots && !timeSlotsError) {
        fields.push(timeFieldLabel)
      }
    }
    if (experience.languages.length > 1) {
      fields.push(languageFieldLabel)
    }
    return fields
  }, [dateFieldLabel, datesError, experience, languageFieldLabel, locationFieldLabel, timeFieldLabel, timeSlotsError])

  const selectedAvailableDate = availableDates.find(availableDate => availableDate.day === date)
  const performanceWarning = isBestAvailableIngressoOffer(experience, selectedAvailableDate)
    ? getIngressoPerformanceWarning(currentTimeSlot)
    : undefined
  const shouldUseArenaSeatSelection = isArenaExperienceOffer(experience, selectedAvailableDate) && !isBuyNowBookLater && !isGift
  const arenaEventId = shouldUseArenaSeatSelection ? experience.providerId : undefined
  const arenaPerformanceId = shouldUseArenaSeatSelection ? currentTimeSlot?.providerData?.performanceId : undefined
  const arenaTicketMismatchMessage = intl.formatMessage({
    id: 'MB3SmPVjYw4L3GBZSyj7',
    defaultMessage: 'Could not match Arena seats to available tickets. Please try again.',
    description: 'Snackbar error shown when selected Ingresso Arena seats cannot be mapped to checkout tickets',
  })

  const handleArenaSelectionComplete = useCallback((selection: ArenaSelectionResult) => {
    if (!currentTimeSlot) {
      releaseIngressoReservationHold(experience.id, selection.reservationToken)
      return
    }

    const arenaTicketCounts = getArenaTicketCounts(selection, currentTimeSlot)
    if (!Object.keys(arenaTicketCounts).length) {
      releaseIngressoReservationHold(experience.id, selection.reservationToken)
      showSnackbar(arenaTicketMismatchMessage, 'critical')
      return
    }

    const ticketsById = arrayToObject(currentTimeSlot.tickets, ticket => ticket.id)
    const arenaCounts = Object.entries(arenaTicketCounts).map(([ticketId, count]) => {
      const ticket = ticketsById[ticketId]
      if (!ticket) return undefined

      return { ticket, count }
    }).filter(isNonNullable)
    if (!arenaCounts.length) {
      releaseIngressoReservationHold(experience.id, selection.reservationToken)
      showSnackbar(arenaTicketMismatchMessage, 'critical')
      return
    }

    const ingressoArena = getArenaProviderBookingData(selection)
    const item = generateExperienceCheckoutItem(
      experience,
      {
        date,
        language: selectedLanguage?.id,
        pickupPoint: selectedPickupPoint?.id,
        redemptionLocation: selectedLocation?.id,
        ticketModesMap: selectedTicketModesMap,
        time,
      },
      arenaCounts,
      {
        orderMode,
        metaData: {
          providerBookingData: {
            ...ingressoArena,
            reservationExpiresAt: selection.reservationExpiresAt,
            reservationToken: selection.reservationToken,
            returnUrl: getExperienceOfferPageURL(experience),
          },
        },
      },
    )

    setTicketCounts(arenaTicketCounts)
    dispatch(addToCheckoutCart([item]))
    trackEvent(addToCart())
    dispatch(navigateToCheckout())
  }, [
    currentTimeSlot,
    arenaTicketMismatchMessage,
    date,
    dispatch,
    experience,
    orderMode,
    selectedLanguage?.id,
    selectedLocation?.id,
    selectedPickupPoint?.id,
    selectedTicketModesMap,
    setTicketCounts,
    time,
  ])

  const { totalUsedSeatsOnCart, totalSeatsRemaining } = useMemo(() => {
    if (!currentTimeSlot) return { totalSeatsRemaining: 0, totalUsedSeatsOnCart: 0, totalUsedSeatsOnServer: 0 }
    return getTotalSeats(currentTimeSlot, ticketCounts)
  }, [ticketCounts, currentTimeSlot])

  const hasError = datesError || timeSlotsError
  const emptyTimeSlots = !!(date && !availableTimes.length)
  // for voucher based sometimes some tickets or all tickets will be sold out
  const offerWithAllTicketsSoldOut = emptyTimeSlots && !hasError && !loading

  const minSeats = currentTimeSlot?.purchaseLimit.min ?? 1

  const showLuxPlusPrice = useMemo(() => {
    return luxPlusEnabled && experience.pricing.memberPrice > 0
  }, [luxPlusEnabled, experience])

  const pointsEarnCalculationRequest = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (isFromPrice) {
      return [generateLoyaltyPointsCalculatorExperienceOptions(experience, {
        price: showLuxPlusPrice ? totalLuxPlusPrice : totalPrice,
      })]
    }

    const ticketsById = arrayToObject(allTicketOptions, ticket => ticket.id)

    const selectedTickets = Object.entries(ticketCounts).flatMap(([id, count]) => {
      const ticket = ticketsById[id]
      if (!ticket) return []

      return Array.from({ length: count }, () => generateLoyaltyPointsCalculatorExperienceOptions(experience, {
        price: showLuxPlusPrice ? ticket.memberPrice || 0 : ticket.price || 0,
      }))
    })

    return selectedTickets
  }, [allTicketOptions, ticketCounts, totalPrice, isFromPrice, showLuxPlusPrice, totalLuxPlusPrice, experience])

  const noTicketsWithFieldsMessage = intl.formatMessage(
    {
      id: 'OGlbZxBIzdSebKVi9kq7',
      defaultMessage: 'There are no tickets available for your selection, try another {fields}.',
      description: 'Error message when no tickets are available, suggesting the user try different selection fields',
    },
    { fields: arrayToStringListFormat(userSelectedFields, 'or', intl) },
  )
  const noTicketsMessage = intl.formatMessage({
    id: '1rwXmWU0tcdEG9L9edZs',
    defaultMessage: 'There are no tickets available for your selection.',
    description: 'Error message when no tickets are available for the selected options',
  })
  const noTicketsErrorMessage = userSelectedFields.length > 0 ? noTicketsWithFieldsMessage : noTicketsMessage
  const noArenaPerformanceMessage = intl.formatMessage({
    id: '6tpkSONcxyyDie7DcYmT',
    defaultMessage: 'Seat selection is unavailable for this time, try another time.',
    description: 'Error message when an Ingresso Arena time slot is missing its provider performance id',
  })
  const warningLabel = intl.formatMessage({
    id: '9uj8JQTS8XAKPTEl5yPp',
    defaultMessage: 'You should add at least one adult ticket',
    description: 'Warning message when the user has not selected any adult experience tickets',
  })
  const requiredTicketsWarningLabel = intl.formatMessage({
    id: 'OIH7ZHyMTwggPAdUP7Cn',
    defaultMessage: 'Please select all required ticket options',
    description: 'Warning message when required experience tickets are not selected',
  })
  const ticketWarningLabel = hasRequiredTicketsError ? requiredTicketsWarningLabel : warningLabel
  return (<VerticalSpacer as="form" gap={20} ref={internalFormRef}>
    {!isBuyNowBookLater && !isGift &&
      <TicketContainerWithoutPadding>
        <LoadingIndicator floating visible={loading} />
        <TicketInputContainer>
          <ExperienceTicketInputs
            experienceView={experienceView}
            onTimeSelect={onSelectTimeSlot}
            onPickupPointSelect={onPickupPointSelect}
            onLocationSelect={onLocationSelect}
            onTicketModeSelect={onTicketModeSelect}
            allDates={availableDates}
            selectedDate={date ?? ''}
            selectedTimeSlot={time ?? ''}
            defaultLanguageId={selectedLanguage?.id}
            defaultPickupPointId={selectedPickupPoint?.id}
            defaultRedemptionLocationId={selectedLocation?.id}
            defaultTicketModesMap={selectedTicketModesMap}
            availableTimes={availableTimes}
            onDateSelect={onDateSelect}
            onLanguageSelect={setLanguage}
            preserveSelectedDateWhenDateOptionsEmpty={preserveSelectedDateWhenDateOptionsEmpty}
            dateOptionsLoading={fetchingDates}
            calendarPresentation={calendarPresentation}
          />
          {(hasError || offerWithAllTicketsSoldOut) && <BodyText variant="large" colour="critical">
            {noTicketsErrorMessage}
          </BodyText>}
        </TicketInputContainer>
        {allTicketOptions.length > 0 && !!currentTimeSlot && <>
          <ExperienceTicketCustomerLimit
            purchaseLimit={currentTimeSlot.purchaseLimit}
            totalSeatsRemaining={totalSeatsRemaining}
            totalUsedSeatsOnCart={totalUsedSeatsOnCart}
          />
          {(hasRequiredTicketsError || (totalNonExtraCountOnCart === 0 && totalCountOnCart > 0) || hasNoTicketsSelected) && (
            <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
              {ticketWarningLabel}
            </MessageBanner>
          )}
        </>}

      </TicketContainerWithoutPadding>}
    <LoadingIndicator floating visible={(isBuyNowBookLater || isGift) && loading} />
    {(isBuyNowBookLater || isGift) && (hasRequiredTicketsError || (totalNonExtraCountOnCart === 0 && totalCountOnCart > 0) || hasNoTicketsSelected) && (
      <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
        {ticketWarningLabel}
      </MessageBanner>
    )}
    {shouldUseArenaSeatSelection && !!currentTimeSlot && !arenaPerformanceId && <BodyText variant="large" colour="critical">
      {noArenaPerformanceMessage}
    </BodyText>}
    {shouldUseArenaSeatSelection && !!currentTimeSlot && !!arenaEventId && !!arenaPerformanceId && <ArenaSeatSelection
      apiBase={config.EXPERIENCES_ARENA_API_BASE}
      displayCurrencyCode={currency}
      eventId={arenaEventId}
      experienceId={experience.id}
      onSelectionComplete={handleArenaSelectionComplete}
      perfId={arenaPerformanceId}
      selectedTimeSlot={currentTimeSlot}
      tyrBase={config.EXPERIENCES_ARENA_TYR_BASE}
    />}
    {!shouldUseArenaSeatSelection && allTicketOptions.length > 0 && <TicketContainerWithoutPadding>
      <LoadingIndicator floating visible={(!isBuyNowBookLater || !isGift) && loading} />
      <TicketInputContainer>
        <ExperienceTicketSelection
          ticketOptions={allTicketOptions}
          ticketCounts={ticketCounts}
          onTicketCountChange={handleTicketCountChange}
          purchaseLimitRemaining={totalSeatsRemaining}
          purchaseLimitReason={currentTimeSlot?.purchaseLimit.maxReason}
          orderMode={orderMode}
          bookingType={experience.bookingType}
          afterHeading={collinsonValidityNote}
          performanceWarning={performanceWarning}
          hideAvailabilityBanner={experience.provider === 'cln'}
          offer={experience}
          viewMoreToggleTopSpacing={viewMoreToggleTopSpacing}
        />
      </TicketInputContainer>
      {(isBuyNowBookLater || isGift) && !!currentTimeSlot && <ExperienceTicketCustomerLimit
        purchaseLimit={currentTimeSlot.purchaseLimit}
        totalSeatsRemaining={totalSeatsRemaining}
        totalUsedSeatsOnCart={totalUsedSeatsOnCart}
      />}
    </TicketContainerWithoutPadding>}
    {!shouldUseArenaSeatSelection && <ExperienceOfferPageBuyBar
      experience={experience}
      totalPrice={totalPrice}
      totalLuxPlusPrice={totalLuxPlusPrice}
      isFromPrice={isFromPrice}
      showLuxPlusPrice={showLuxPlusPrice}
      pointsEarnCalculationRequest={pointsEarnCalculationRequest}
      userMultiItemCartSupported={userMultiItemCartSupported}
      isWeddingGiftRegistry={isWeddingGiftRegistryTicketSelection}
      isReservingTickets={isReservingTickets}
      onBuyNow={handleBuyNow}
      onAddToUserCart={handleAddToUserCart}
      onAddToWeddingGiftRegistryCart={handleAddToWeddingGiftRegistryCart}
      secondaryAction={secondaryAction}
    />}
  </VerticalSpacer>
  )
})

const mapStateToProps = (state: App.State) => {
  return {
    luxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(React.memo(ExperienceOfferPageTicketSelection))
